import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ProgressBar } from 'antd-mobile';
import { ClockCircleOutline, PlayOutline, CheckCircleOutline, LeftOutline } from 'antd-mobile-icons';
import { useNavigate, useParams } from 'react-router-dom';
import TextLightScroll from '../../components/texts/TextLightScroll';

const PracticeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string | number }>({});
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15分钟
  const [isStarted, setIsStarted] = useState(false);
  const [score, setScore] = useState(0);

  // 模拟练习详情数据
  const practiceDetail = {
    id: 1,
    title: 'JavaScript基础语法挑战',
    description: '通过10道精选题目，快速检验你的JavaScript基础知识掌握程度。包含变量声明、函数定义、作用域、闭包等核心概念。',
    type: 'quiz',
    category: '前端开发',
    difficulty: '初级',
    duration: 15,
    totalQuestions: 10,
    participants: 2341,
    averageScore: 85,
    tags: ['JavaScript', '基础语法', '变量', '函数', '作用域', '闭包'],
    thumbnail: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400&h=250&fit=crop',
    points: 100,
    passingScore: 70,
    attempts: 0,
    bestScore: 0,
    instructor: {
      name: '张老师',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face',
      title: '前端开发专家',
      experience: '8年开发经验'
    },
    learningObjectives: [
      '掌握JavaScript变量声明的不同方式',
      '理解函数的定义和调用机制',
      '熟悉作用域和闭包的概念',
      '能够编写基础的JavaScript代码'
    ],
    prerequisites: [
      '具备基本的编程概念',
      '了解HTML和CSS基础',
      '有一定的逻辑思维能力'
    ],
    questions: [
      {
        id: 1,
        type: 'single_choice',
        question: '以下哪种方式是正确的JavaScript变量声明？',
        options: [
          'var name = "John";',
          'variable name = "John";',
          'v name = "John";',
          'declare name = "John";'
        ],
        correctAnswer: 0,
        explanation: 'var是JavaScript中声明变量的关键字之一，除此之外还有let和const。'
      },
      {
        id: 2,
        type: 'single_choice',
        question: '在JavaScript中，以下哪个不是基本数据类型？',
        options: [
          'string',
          'number',
          'array',
          'boolean'
        ],
        correctAnswer: 2,
        explanation: 'array是引用数据类型，不是基本数据类型。JavaScript的基本数据类型包括string、number、boolean、undefined、null、symbol和bigint。'
      },
      {
        id: 3,
        type: 'single_choice',
        question: '以下哪个函数声明是正确的？',
        options: [
          'function myFunc() { }',
          'func myFunc() { }',
          'def myFunc() { }',
          'method myFunc() { }'
        ],
        correctAnswer: 0,
        explanation: 'function是JavaScript中声明函数的关键字。'
      },
      {
        id: 4,
        type: 'single_choice',
        question: '什么是闭包？',
        options: [
          '一种循环结构',
          '一种条件语句',
          '函数和其词法环境的组合',
          '一种数据类型'
        ],
        correctAnswer: 2,
        explanation: '闭包是函数和声明该函数的词法环境的组合。'
      },
      {
        id: 5,
        type: 'single_choice',
        question: 'JavaScript中如何声明一个常量？',
        options: [
          'const PI = 3.14;',
          'constant PI = 3.14;',
          'final PI = 3.14;',
          'static PI = 3.14;'
        ],
        correctAnswer: 0,
        explanation: 'const关键字用于声明常量，一旦赋值后不能再修改。'
      }
    ],
    leaderboard: [
      {
        rank: 1,
        name: '小明',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
        score: 95,
        time: '8分钟'
      },
      {
        rank: 2,
        name: '小红',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face',
        score: 90,
        time: '10分钟'
      },
      {
        rank: 3,
        name: '小李',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
        score: 88,
        time: '12分钟'
      }
    ]
  };

  const tabs = [
    { key: 'overview', title: '练习概述' },
    { key: 'questions', title: '开始练习' },
    { key: 'leaderboard', title: '排行榜' }
  ];

  // 计时器效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isStarted && timeLeft > 0 && !showResult) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isStarted, timeLeft, showResult]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartPractice = () => {
    setIsStarted(true);
    setActiveTab('questions');
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setTimeLeft(practiceDetail.duration * 60);
  };

  const handleAnswerSelect = (questionId: number, answer: string | number) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNextQuestion = () => {
    if (currentQuestion < practiceDetail.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    // 计算分数
    let correctCount = 0;
    practiceDetail.questions.forEach((question, index) => {
      if (answers[question.id] === question.correctAnswer) {
        correctCount++;
      }
    });
    const finalScore = Math.round((correctCount / practiceDetail.questions.length) * 100);
    setScore(finalScore);
    setShowResult(true);
    setIsStarted(false);
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setIsStarted(false);
    setTimeLeft(practiceDetail.duration * 60);
    setScore(0);
  };

  if (!practiceDetail) {
    return (
      <StyledWrapper>
        <div className="practice-detail-container">
          <div className="error-container">
            <h2>练习未找到</h2>
            <button className="back-btn" onClick={() => navigate('/learn/practice')}>返回练习列表</button>
          </div>
        </div>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper>
      <div className="practice-detail-container">
        {/* 头部导航 */}
        <div className="header">
          <button className="back-btn" onClick={() => navigate('/learn/practice')}>
            <LeftOutline /> 返回
          </button>
          {isStarted && (
            <div className={`timer ${timeLeft < 60 ? 'warning' : ''}`}>
              <ClockCircleOutline />
              <span>{formatTime(timeLeft)}</span>
            </div>
          )}
        </div>

        {/* 练习信息卡片 */}
        <div className="practice-info-card">
          <div className="practice-header">
            <div className="practice-thumbnail">
              <img src={practiceDetail.thumbnail} alt={practiceDetail.title} />
              <div className="type-badge">
                {practiceDetail.type === 'quiz' ? '📝' : '💻'}
              </div>
            </div>
            
            <div className="practice-meta">
              <TextLightScroll text={practiceDetail.title} className="practice-title" />
              <p className="practice-description">{practiceDetail.description}</p>
              
              <div className="meta-tags">
                <span className="category-tag">{practiceDetail.category}</span>
                <span className="difficulty-tag">{practiceDetail.difficulty}</span>
              </div>
              
              <div className="stats-row">
                <div className="stat">
                  <span className="label">时长</span>
                  <span className="value">{practiceDetail.duration}分钟</span>
                </div>
                <div className="stat">
                  <span className="label">题目数</span>
                  <span className="value">{practiceDetail.totalQuestions}题</span>
                </div>
                <div className="stat">
                  <span className="label">参与人数</span>
                  <span className="value">{practiceDetail.participants}</span>
                </div>
                <div className="stat">
                  <span className="label">平均分</span>
                  <span className="value">{practiceDetail.averageScore}分</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 标签页内容 */}
        <div className="tabs-container">
          <div className="tabs-header">
            {tabs.map(tab => (
              <button 
                key={tab.key}
                className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                disabled={tab.key === 'questions' && !isStarted && !showResult}
              >
                {tab.title}
              </button>
            ))}
          </div>
          
          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-content">
                {/* 讲师信息 */}
                <div className="instructor-card">
                  <h3>讲师介绍</h3>
                  <div className="instructor-info">
                    <img src={practiceDetail.instructor.avatar} alt={practiceDetail.instructor.name} className="instructor-avatar" />
                    <div className="instructor-details">
                      <h4>{practiceDetail.instructor.name}</h4>
                      <p>{practiceDetail.instructor.title}</p>
                      <span>{practiceDetail.instructor.experience}</span>
                    </div>
                  </div>
                </div>

                {/* 学习目标 */}
                <div className="objectives-card">
                  <h3>学习目标</h3>
                  <ul className="objectives-list">
                    {practiceDetail.learningObjectives.map((objective, index) => (
                      <li key={index}>
                        <CheckCircleOutline className="check-icon" />
                        {objective}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 前置要求 */}
                <div className="prerequisites-card">
                  <h3>前置要求</h3>
                  <ul className="prerequisites-list">
                    {practiceDetail.prerequisites.map((prerequisite, index) => (
                      <li key={index}>
                        <CheckCircleOutline className="check-icon" />
                        {prerequisite}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 标签 */}
                <div className="tags-card">
                  <h3>相关标签</h3>
                  <div className="tags-container">
                    {practiceDetail.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>

                {/* 开始练习 */}
                <div className="start-section">
                  <button className="start-btn" onClick={handleStartPractice}>
                    <PlayOutline /> 开始练习
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'questions' && (
              <div className="questions-content">
                {!isStarted && !showResult ? (
                  <div className="start-prompt">
                    <h3>准备开始练习</h3>
                    <p>点击下方按钮开始你的JavaScript基础语法挑战</p>
                    <button className="start-btn" onClick={handleStartPractice}>
                      <PlayOutline /> 开始练习
                    </button>
                  </div>
                ) : showResult ? (
                  <div className="result-content">
                    <div className="result-icon">
                      {score >= practiceDetail.passingScore ? '🎉' : '😔'}
                    </div>
                    <h2>{score >= practiceDetail.passingScore ? '恭喜通过！' : '继续努力！'}</h2>
                    <div className="score-display">
                      <span className="score-number">{score}</span>
                      <span className="score-label">分</span>
                    </div>
                    <div className="result-stats">
                      <div className="result-stat">
                        正确率: {Math.round((score / 100) * practiceDetail.questions.length)}/{practiceDetail.questions.length}
                      </div>
                      <div className="result-stat">
                        用时: {formatTime(practiceDetail.duration * 60 - timeLeft)}
                      </div>
                      <div className="result-stat">
                        及格线: {practiceDetail.passingScore}分
                      </div>
                    </div>
                    <div className="result-actions">
                      <button className="retry-btn" onClick={handleRestart}>
                        重新练习
                      </button>
                      <button className="back-btn" onClick={() => navigate('/learn/practice')}>
                        返回列表
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* 进度条 */}
                    <div className="progress-section">
                      <div className="progress-info">
                        <span>题目 {currentQuestion + 1} / {practiceDetail.questions.length}</span>
                        <span>已完成 {Math.round(((currentQuestion + 1) / practiceDetail.questions.length) * 100)}%</span>
                      </div>
                      <ProgressBar 
                        percent={((currentQuestion + 1) / practiceDetail.questions.length) * 100}
                        style={{
                          '--track-width': '8px',
                          '--fill-color': '#007bff'
                        }}
                      />
                    </div>

                    {/* 当前题目 */}
                    <div className="question-card">
                      <div className="question-header">
                        <h3>第 {currentQuestion + 1} 题</h3>
                        <span className="question-type">
                          {practiceDetail.questions[currentQuestion].type === 'single_choice' ? '单选题' : '编程题'}
                        </span>
                      </div>
                      
                      <div className="question-text">
                        {practiceDetail.questions[currentQuestion].question}
                      </div>

                      {practiceDetail.questions[currentQuestion].type === 'single_choice' ? (
                        <div className="options-container">
                          {practiceDetail.questions[currentQuestion].options?.map((option, index) => (
                            <div 
                              key={index}
                              className={`option ${
                                answers[practiceDetail.questions[currentQuestion].id] === index ? 'selected' : ''
                              }`}
                              onClick={() => handleAnswerSelect(practiceDetail.questions[currentQuestion].id, index)}
                            >
                              <div className="option-label">
                                {String.fromCharCode(65 + index)}
                              </div>
                              <div className="option-text">{option}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="coding-container">
                          <textarea 
                            className="code-input"
                            placeholder="请在此处编写你的代码..."
                            value={answers[practiceDetail.questions[currentQuestion].id] as string || ''}
                            onChange={(e) => handleAnswerSelect(practiceDetail.questions[currentQuestion].id, e.target.value)}
                            rows={10}
                          />
                        </div>
                      )}
                    </div>

                    {/* 导航按钮 */}
                    <div className="navigation-buttons">
                      <button 
                        className="nav-btn prev-btn"
                        onClick={handlePrevQuestion}
                        disabled={currentQuestion === 0}
                      >
                        上一题
                      </button>
                      <button 
                        className="nav-btn next-btn"
                        onClick={handleNextQuestion}
                        disabled={!answers[practiceDetail.questions[currentQuestion].id]}
                      >
                        {currentQuestion === practiceDetail.questions.length - 1 ? '提交答案' : '下一题'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="leaderboard-content">
                <div className="leaderboard-card">
                  <h3>排行榜</h3>
                  <div className="leaderboard-list">
                    {practiceDetail.leaderboard.map((user, index) => (
                      <div key={index} className="leaderboard-item">
                        <div className="user-info">
                          <span className="rank">#{user.rank}</span>
                          <img src={user.avatar} alt={user.name} className="rank-avatar" />
                          <span className="name">{user.name}</span>
                        </div>
                        <div className="score-info">
                          <span className="score">{user.score}分</span>
                          <span className="time">{user.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .practice-detail-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    background: #f5f5f5;
    min-height: 100vh;
    color: #333;
  }

  .error-container {
    text-align: center;
    padding: 100px 20px;
    
    h2 {
      margin-bottom: 20px;
      color: #333;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    .back-btn {
      background: white;
      color: #666;
      border: 1px solid #e0e0e0;
      padding: 8px 16px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;
      
      &:hover {
        background: #f8f9fa;
      }
    }
    
    .timer {
      display: flex;
      align-items: center;
      gap: 8px;
      color: white;
      background: #007bff;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 600;
      
      &.warning {
        background: #dc3545;
        animation: pulse 1s infinite;
      }
    }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .practice-info-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    margin-bottom: 20px;
    overflow: hidden;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .practice-header {
    display: flex;
    gap: 20px;
    
    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .practice-thumbnail {
    position: relative;
    width: 200px;
    height: 120px;
    flex-shrink: 0;
    border-radius: 12px;
    overflow: hidden;
    
    @media (max-width: 768px) {
      width: 100%;
      height: 180px;
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .type-badge {
      position: absolute;
      top: 10px;
      left: 10px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 4px 8px;
      border-radius: 8px;
      font-size: 1.2rem;
    }
  }

  .practice-meta {
    flex: 1;
    
    .practice-title {
      font-size: 1.5rem;
      font-weight: bold;
      margin: 0 0 10px 0;
      color: #333;
    }
    
    .practice-description {
      font-size: 0.95rem;
      line-height: 1.5;
      color: #666;
      margin-bottom: 15px;
    }
    
    .meta-tags {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 15px;
      
      .category-tag {
        background: #e3f2fd;
        color: #1976d2;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.8rem;
        border: 1px solid #bbdefb;
      }
      
      .difficulty-tag {
        background: #fff3e0;
        color: #f57c00;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.8rem;
        border: 1px solid #ffcc02;
      }
    }
    
    .stats-row {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      
      .stat {
        display: flex;
        flex-direction: column;
        gap: 2px;
        
        .label {
          font-size: 0.8rem;
          color: #666;
        }
        
        .value {
          font-weight: 600;
          color: #007bff;
        }
      }
    }
  }

  .tabs-container {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .tabs-header {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
    
    .tab-button {
      flex: 1;
      padding: 16px;
      border: none;
      background: #f8f9fa;
      color: #666;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s;
      
      &:hover:not(:disabled) {
        background: #e9ecef;
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      &.active {
        background: white;
        color: #007bff;
        border-bottom: 2px solid #007bff;
      }
    }
  }

  .tab-content {
    padding: 24px;
  }

  .overview-content {
    .instructor-card,
    .objectives-card,
    .prerequisites-card,
    .tags-card {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 20px;
      
      h3 {
        color: #007bff;
        margin-bottom: 15px;
        font-size: 1.1rem;
      }
    }
    
    .instructor-info {
      display: flex;
      align-items: center;
      gap: 15px;
      
      .instructor-avatar {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        object-fit: cover;
      }
      
      .instructor-details {
        h4 {
          margin: 0 0 5px 0;
          color: #333;
        }
        
        p {
          margin: 0 0 3px 0;
          color: #007bff;
          font-size: 0.9rem;
        }
        
        span {
          font-size: 0.8rem;
          color: #666;
        }
      }
    }
    
    .objectives-list,
    .prerequisites-list {
      list-style: none;
      padding: 0;
      
      li {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        font-size: 0.95rem;
        
        .check-icon {
          color: #4CAF50;
          flex-shrink: 0;
        }
      }
    }
    
    .tags-container {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      
      .tag {
        background: #e8f5e8;
        color: #4CAF50;
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 0.8rem;
        border: 1px solid #c8e6c9;
      }
    }
    
    .start-section {
      margin-top: 30px;
      text-align: center;
      
      .start-btn {
        background: #007bff;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        
        &:hover {
          background: #0056b3;
        }
      }
    }
  }

  .questions-content {
    .start-prompt {
      text-align: center;
      padding: 40px 20px;
      
      h3 {
        color: #007bff;
        margin-bottom: 10px;
      }
      
      p {
        color: #666;
        margin-bottom: 25px;
      }
      
      .start-btn {
        background: #007bff;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        
        &:hover {
          background: #0056b3;
        }
      }
    }
    
    .progress-section {
      margin-bottom: 20px;
      
      .progress-info {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        font-size: 0.9rem;
        color: #666;
      }
    }
    
    .question-card {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 20px;
      
      .question-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        
        h3 {
          color: #007bff;
          margin: 0;
        }
        
        .question-type {
          background: #e3f2fd;
          color: #1976d2;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.8rem;
        }
      }
      
      .question-text {
        font-size: 1.1rem;
        line-height: 1.5;
        margin-bottom: 20px;
        color: #333;
      }
      
      .options-container {
        .option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          margin-bottom: 8px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          
          &:hover {
            background: #f8f9fa;
            border-color: #007bff;
          }
          
          &.selected {
            background: #e3f2fd;
            border-color: #007bff;
          }
          
          .option-label {
            width: 24px;
            height: 24px;
            background: #f0f0f0;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            flex-shrink: 0;
            color: #666;
          }
          
          &.selected .option-label {
            background: #007bff;
            color: white;
          }
          
          .option-text {
            flex: 1;
            line-height: 1.4;
            color: #333;
          }
        }
      }
      
      .coding-container {
        .code-input {
          width: 100%;
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
          color: #333;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          resize: vertical;
          
          &::placeholder {
            color: #999;
          }
          
          &:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
          }
        }
      }
    }
    
    .navigation-buttons {
      display: flex;
      justify-content: space-between;
      gap: 15px;
      
      .nav-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        &.prev-btn {
          background: white;
          color: #666;
          border: 1px solid #e0e0e0;
          
          &:hover:not(:disabled) {
            background: #f8f9fa;
          }
        }
        
        &.next-btn {
          background: #007bff;
          color: white;
          border: none;
          
          &:hover:not(:disabled) {
            background: #0056b3;
          }
        }
      }
    }
  }

  .result-content {
    text-align: center;
    padding: 20px;
    
    .result-icon {
      font-size: 4rem;
      margin-bottom: 15px;
    }
    
    h2 {
      color: #333;
      margin-bottom: 20px;
    }
    
    .score-display {
      margin-bottom: 20px;
      
      .score-number {
        font-size: 3rem;
        font-weight: bold;
        color: #4CAF50;
      }
      
      .score-label {
        font-size: 1.2rem;
        color: #666;
        margin-left: 5px;
      }
    }
    
    .result-stats {
      margin-bottom: 25px;
      
      .result-stat {
        margin-bottom: 8px;
        color: #666;
      }
    }
    
    .result-actions {
      display: flex;
      gap: 10px;
      justify-content: center;
      
      .retry-btn {
        background: #007bff;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
        
        &:hover {
          background: #0056b3;
        }
      }
      
      .back-btn {
        background: white;
        color: #666;
        border: 1px solid #e0e0e0;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
        
        &:hover {
          background: #f8f9fa;
        }
      }
    }
  }

  .leaderboard-content {
    .leaderboard-card {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      padding: 20px;
      
      h3 {
        color: #007bff;
        margin-bottom: 15px;
        text-align: center;
      }
      
      .leaderboard-list {
        .leaderboard-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 0;
          border-bottom: 1px solid #e0e0e0;
          
          &:last-child {
            border-bottom: none;
          }
          
          .user-info {
            display: flex;
            align-items: center;
            gap: 10px;
            
            .rank {
              font-weight: 600;
              color: #007bff;
              min-width: 30px;
            }
            
            .rank-avatar {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
            }
            
            .name {
              color: #333;
              font-weight: 500;
            }
          }
          
          .score-info {
            text-align: right;
            
            .score {
              display: block;
              font-weight: 600;
              color: #4CAF50;
            }
            
            .time {
              font-size: 0.8rem;
              color: #666;
            }
          }
        }
      }
    }
  }
`;

export default PracticeDetail;